import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getToken, onMessage } from 'firebase/messaging';
import { getFirebaseMessaging, VAPID_KEY } from './FirebaseInit';
import { registerAppServiceWorker } from './swRegistration';
import { registerPushToken } from '../Service/service';

const DISMISSED_KEY = 'PUSH_BANNER_DISMISSED_AT';
const TOKEN_KEY = 'PUSH_FCM_TOKEN';
const DISMISS_BACKOFF_DAYS = 30;

function bannerDismissedRecently() {
  const raw = localStorage.getItem(DISMISSED_KEY);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  if (Number.isNaN(ts)) return false;
  const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  return ageDays < DISMISS_BACKOFF_DAYS;
}

async function fetchAndRegisterToken(currentUser) {
  const messaging = await getFirebaseMessaging();
  if (!messaging) throw new Error('Messaging not supported in this browser');

  // Trigger the app-wide registration if it hasn't happened yet (idempotent).
  await registerAppServiceWorker();

  // CRITICAL: use the registration from `navigator.serviceWorker.ready`, NOT
  // the one returned by .register(). The latter can still be in 'installing'
  // state, in which case its pushManager isn't usable yet and Firebase's
  // getToken() throws "Cannot read properties of undefined (reading
  // 'pushManager')". `.ready` only resolves once the SW is activated.
  const registration = await navigator.serviceWorker.ready;
  if (!registration) throw new Error('Service worker registration unavailable');

  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) throw new Error('FCM returned no token (permission may have just been revoked)');

  const prev = localStorage.getItem(TOKEN_KEY);
  if (prev !== token) {
    try {
      await registerPushToken(token, navigator.userAgent);
    } catch (err) {
      // Backend route may not exist yet during Phase 1 dev — keep token cached locally
      console.warn('[push] /push/register failed (backend pending?)', err);
    }
    localStorage.setItem(TOKEN_KEY, token);
  }
  return token;
}

export default function usePushNotifications(currentUser) {
  // permission: 'unknown' | 'default' | 'granted' | 'denied' | 'unsupported'
  const [permission, setPermission] = useState('unknown');
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof Notification === 'undefined') {
      setPermission('unsupported');
      return;
    }
    if (!('serviceWorker' in navigator)) {
      setPermission('unsupported');
      return;
    }
    setPermission(Notification.permission);
  }, []);

  useEffect(() => {
    if (!currentUser || permission !== 'default' || bannerDismissedRecently()) {
      setShowBanner(false);
      return;
    }
    setShowBanner(true);
  }, [currentUser, permission]);

  // Silently refresh token when already granted
  const empId = currentUser && currentUser.emp_id;
  useEffect(() => {
    if (permission !== 'granted' || !empId) return;
    fetchAndRegisterToken(currentUser).catch((err) => {
      console.warn('[push] silent re-register failed', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, empId]);

  // Foreground messages → toast
  useEffect(() => {
    if (permission !== 'granted') return undefined;
    let unsubscribe;
    (async () => {
      const messaging = await getFirebaseMessaging();
      if (!messaging) return;
      unsubscribe = onMessage(messaging, ({ notification, data }) => {
        if (!notification) return;
        const title = notification.title || 'Notification';
        const body = notification.body || '';
        toast.info(`${title}${body ? ' — ' + body : ''}`, {
          onClick: () => {
            if (data && data.link) window.location.href = data.link;
          },
        });
      });
    })();
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [permission]);

  const enable = useCallback(async () => {
    if (permission === 'unsupported') return { ok: false, reason: 'unsupported' };

    let next = Notification.permission;
    if (next === 'default') {
      next = await Notification.requestPermission();
    }
    setPermission(next);

    if (next !== 'granted') {
      if (next === 'denied') {
        localStorage.setItem(DISMISSED_KEY, String(Date.now()));
        setShowBanner(false);
      }
      return { ok: false, reason: next };
    }

    try {
      const token = await fetchAndRegisterToken(currentUser);
      setShowBanner(false);
      return { ok: true, token };
    } catch (err) {
      console.error('[push] enable failed', err);
      return { ok: false, reason: 'token_error', error: err };
    }
  }, [permission, currentUser]);

  const dismissBanner = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setShowBanner(false);
  }, []);

  return { permission, showBanner, enable, dismissBanner };
}
